// What Bailey's widgets need from the salon: its brand (as Marketing › Config
// keeps it), its market and links, and a memory of what was done with each
// widget (a saved draft) so reopening a conversation shows it as done.
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { salonService } from '../../../../_services/salon.service'
import { loadBrandKit, saveBrandKit, kitFromSalon, marketFor, normHex } from '../../../marketing-v2/features/email-composer/email/theme'
import { MARKET_LOOK } from '../../../marketing-v2/features/email-composer/data/templates'
import { salonLinks } from '../../../marketing-v2/features/email-composer/email/links'
import { hydrateBrandKit, pushBrandKit } from '../../../marketing-v2/features/brand/brandKitSync'

const salonIdNow = () => { try { return localStorage.getItem('salonId') || '' } catch (e) { return '' } }

// The salon's settings, read once for every widget in a conversation
let salonRead = null
const readSalon = (salonId) => {
  if (salonRead && salonRead.id === salonId && Date.now() - salonRead.at < 5 * 60 * 1000) return salonRead.job
  const job = salonService.getSalonSettings(salonId)
    .then((res) => {
      const d = res && res.data
      const rd = d && (d.responseData || d)
      return rd && rd.data && typeof rd.data === 'object' ? rd.data : null
    })
    .catch(() => null)
  salonRead = { id: salonId, at: Date.now(), job }
  return job
}

// Brand saves anywhere (a widget, Marketing › Config) reach every widget
const BRAND_EVENT = 'beu-bailey-brand'
// and the server, so Bailey and every device see it (this used to stay on
// the device: Bailey's brand_kit tool never saw it and asked again)
export const saveBrand = (salonId, kit) => {
  const saved = saveBrandKit(salonId, kit)
  pushBrandKit(salonId, kit)
  try { window.dispatchEvent(new CustomEvent(BRAND_EVENT)) } catch (e) { /* old browsers */ }
  return saved
}
// Put the brand back exactly as it was (no kit at all, when there wasn't one)
export const restoreBrand = (salonId, kit) => {
  if (kit && Object.keys(kit).length) return saveBrand(salonId, kit)
  try { localStorage.removeItem(`beu-email-brand-${salonId}`) } catch (e) { /* private mode */ }
  // ...and on the server: there was no kit, so clear what the save set (the
  // tone stays unless the voice undo clears it too)
  pushBrandKit(salonId, { accent: null, display: null, text: null, look: null, logo: null })
  try { window.dispatchEvent(new CustomEvent(BRAND_EVENT)) } catch (e) { /* old browsers */ }
  return {}
}

// The salon's brand as every Beu tool sees it: the saved brand kit over what
// the salon's settings suggest, with the market's look when none is chosen
export const useBrand = () => {
  const salonId = salonIdNow()
  const hairdresser = useSelector(s => (s && s.user && s.user.userInfo && s.user.userInfo.hairdresser) || null)
  const [salon, setSalon] = useState(null)
  const [ready, setReady] = useState(false)
  const [saved, setSaved] = useState(() => loadBrandKit(salonId))
  const [extras, setExtras] = useState(() => loadBrandExtras(salonId))
  useEffect(() => {
    let alive = true
    if (salonId) {
      readSalon(salonId).then((s) => {
        if (!alive) return
        // The server's kit wins (it may have been set on another device), and
        // the voice in it becomes this device's too
        const kit = hydrateBrandKit(salonId, s)
        if (kit) setSaved(kit)
        if (hydrateBrandExtras(salonId, s)) setExtras(loadBrandExtras(salonId))
        setSalon(s)
        setReady(true)
      })
    }
    if (!salonId) setReady(true)
    const again = () => { setSaved(loadBrandKit(salonId)); setExtras(loadBrandExtras(salonId)) }
    const onStorage = (e) => { if (e.key && (e.key.indexOf('beu-email-brand-') === 0 || e.key.indexOf('beu-brand-extras-') === 0)) again() }
    window.addEventListener(BRAND_EVENT, again)
    window.addEventListener('storage', onStorage)
    return () => { alive = false; window.removeEventListener(BRAND_EVENT, again); window.removeEventListener('storage', onStorage) }
  }, [salonId])
  const userSalon = (hairdresser && hairdresser.salon) || {}
  const market = marketFor((salon && salon.businessType) || userSalon.businessType) || 'hair'
  const spec = useMemo(() => {
    const k = { ...(salon ? kitFromSalon(salon) : {}), ...(saved || {}) }
    return { ...k, look: k.look || MARKET_LOOK[market] || 'gloss' }
  }, [salon, saved, market])
  const salonName = spec.name || (salon && salon.salonName) || userSalon.salonName || ''
  const kit = useMemo(() => ({
    name: salonName || 'Your salon',
    accent: normHex(spec.accent) || null,
    logo: spec.logo || null,
    display: spec.display || null,
    text: spec.text || null,
  }), [salonName, spec.accent, spec.logo, spec.display, spec.text])
  const links = useMemo(() => salonLinks(hairdresser), [hairdresser])
  return { salonId, salon, ready, spec, saved, extras, kit, market, salonName, links, isSet: !!(saved && Object.keys(saved).length) }
}

// The salon's tone of voice and the rest of its palette, as the salon
// confirmed them with Bailey. Kept on this device for every screen to read,
// and in the salon's brand kit on the server (tone, palette, toneConfirmedAt)
// so Bailey's brand_kit tool and other devices have them.
const EXTRAS = (salonId) => `beu-brand-extras-${salonId || salonIdNow() || 'me'}`
export const loadBrandExtras = (salonId) => { try { return JSON.parse(localStorage.getItem(EXTRAS(salonId))) || {} } catch (e) { return {} } }
// The voice as the server's kit holds it: null clears what isn't there
const voiceOf = extras => ({
  tone: (extras && extras.tone) || null,
  palette: (extras && extras.palette && extras.palette.length) ? extras.palette : null,
  toneConfirmedAt: (extras && extras.tone && extras.confirmedAt) || null,
})
const pushVoice = (salonId, extras) => pushBrandKit(salonId, { ...(loadBrandKit(salonId) || {}), ...voiceOf(extras) })
export const saveBrandExtras = (salonId, extras) => {
  const next = { ...extras, confirmedAt: new Date().toISOString() }
  try { localStorage.setItem(EXTRAS(salonId), JSON.stringify(next)) } catch (e) { /* private mode */ }
  pushVoice(salonId, next)
  try { window.dispatchEvent(new CustomEvent(BRAND_EVENT)) } catch (e) { /* old browsers */ }
  return next
}
// Take the voice from the server's kit when this device doesn't have it (or
// has an older one). Returns true when it changed anything.
export const hydrateBrandExtras = (salonId, salon) => {
  const kit = salon && salon.emailBrandKit
  if (!kit || !kit.tone) return false
  const mine = loadBrandExtras(salonId)
  const theirsAt = Date.parse(kit.toneConfirmedAt || '') || 0
  const mineAt = Date.parse(mine.confirmedAt || '') || 0
  if (mine.tone === kit.tone || (mine.tone && mineAt >= theirsAt)) return false
  const next = { ...mine, tone: kit.tone, ...(kit.palette && kit.palette.length ? { palette: kit.palette } : {}), confirmedAt: kit.toneConfirmedAt || new Date().toISOString() }
  try { localStorage.setItem(EXTRAS(salonId), JSON.stringify(next)) } catch (e) { return false }
  return true
}
export const restoreBrandExtras = (salonId, extras) => {
  try {
    if (extras && Object.keys(extras).length) localStorage.setItem(EXTRAS(salonId), JSON.stringify(extras))
    else localStorage.removeItem(EXTRAS(salonId))
  } catch (e) { /* private mode */ }
  pushVoice(salonId, extras)
  try { window.dispatchEvent(new CustomEvent(BRAND_EVENT)) } catch (e) { /* old browsers */ }
}

// ─── What was done with a widget ───
const MEMO = () => `beu-bailey-widgets-${salonIdNow() || 'me'}`
const readAll = () => { try { return JSON.parse(localStorage.getItem(MEMO())) || {} } catch (e) { return {} } }
export const recall = (id) => (id ? readAll()[id] || null : null)
export const remember = (id, value) => {
  if (!id) return
  const all = readAll()
  all[id] = { ...(all[id] || {}), ...value, at: Date.now() }
  // Keep the newest 200
  const keys = Object.keys(all).sort((a, b) => (all[b].at || 0) - (all[a].at || 0))
  const kept = keys.slice(0, 200).reduce((o, k) => ({ ...o, [k]: all[k] }), {})
  try { localStorage.setItem(MEMO(), JSON.stringify(kept)) } catch (e) { /* private mode */ }
}
export const useRecall = (id) => {
  const [value, setValue] = useState(() => recall(id))
  const set = (v) => { remember(id, v); setValue(recall(id)) }
  return [value, set]
}
